import type { KeyboardEvent, ReactNode } from 'react';
import { useLanguage } from '../../../contexts/LanguageContext';

type EmployerTaskSubPageHeaderProps = {
  title: string;
  subtitle?: string;
  onBack: () => void;
  children?: ReactNode;
};

export const EmployerTaskSubPageHeader = ({
  title,
  subtitle,
  onBack,
  children,
}: EmployerTaskSubPageHeaderProps) => {
  const { t } = useLanguage();

  const handleBackKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onBack();
    }
  };

  return (
    <header className="z-10 border-b border-gray-200/80 bg-white dark:border-gray-800 dark:bg-gray-900">
      <div className="mx-auto max-w-lg px-4 py-4">
        <div className="flex min-w-0 items-center gap-3">
          <button
            type="button"
            onClick={onBack}
            onKeyDown={handleBackKeyDown}
            tabIndex={0}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-gray-600 hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:text-gray-300 dark:hover:bg-gray-800"
            aria-label={t('task.action.back')}
          >
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div className="min-w-0 flex-1">
            <h1 className="text-xl font-bold tracking-tight text-gray-900 dark:text-gray-100">{title}</h1>
            {subtitle ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">{subtitle}</p>
            ) : null}
          </div>
          {children}
        </div>
      </div>
    </header>
  );
};
